/**
 * TimeInput — single masked text field for birth-time entry (HH:MM:SS).
 *
 * The user types only digits; colons are inserted automatically:
 *   "1" → "1"
 *   "18" → "18"
 *   "181" → "18:1"
 *   "1814" → "18:14"
 *   "18143" → "18:14:3"
 *   "181434" → "18:14:34"
 *
 * Seconds are optional — the caller (BirthChartForm) appends ":00" at
 * submit time when the user leaves SS empty.
 *
 * Backspace through auto-inserted colons works naturally: deleting a ":"
 * also removes the preceding digit so the cursor never gets stuck.
 *
 * Value contract: emits "HH:MM:SS" once fully entered, partial strings
 * while typing. parseTime() in chart.ts accepts "HH:MM" and "HH:MM:SS".
 */

import { useRef } from "react";

type Props = {
  id: string;
  /** Current formatted value, e.g. "18:14:34" or partial "18:1". */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  hint?: string;
};

/** Insert colons after HH and MM positions. */
function applyTimeMask(digits: string): string {
  const d = digits.slice(0, 6);
  if (d.length > 4) return `${d.slice(0, 2)}:${d.slice(2, 4)}:${d.slice(4)}`;
  if (d.length > 2) return `${d.slice(0, 2)}:${d.slice(2)}`;
  return d;
}

function validate(v: string): string | null {
  const parts = v.split(":");
  // Only validate when SS segment is complete (all 6 digits typed).
  if (parts.length !== 3 || parts[2].length !== 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const s = parseInt(parts[2], 10);
  if (isNaN(h) || isNaN(m) || isNaN(s)) return "Ungültige Zeit";
  if (h > 23) return "Stunden 00–23";
  if (m > 59) return "Minuten 00–59";
  if (s > 59) return "Sekunden 00–59";
  return null;
}

export function TimeInput({ id, value, onChange, required = false, hint }: Props) {
  // Track the last emitted value to detect separator-deletion on backspace.
  const prevRef = useRef(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const prev = prevRef.current;

    const newDigits = raw.replace(/\D/g, "");
    const prevDigits = prev.replace(/\D/g, "");

    let digits = newDigits.slice(0, 6);

    // Backspace on a separator: digit count unchanged but string shrank →
    // remove one extra digit so the separator disappears instead of
    // being immediately re-inserted.
    if (newDigits.length === prevDigits.length && raw.length < prev.length) {
      digits = newDigits.slice(0, -1);
    }

    const masked = applyTimeMask(digits);
    prevRef.current = masked;
    onChange(masked);
  };

  const isComplete = value.length === 8; // "HH:MM:SS"
  const error = isComplete ? validate(value) : null;

  return (
    <div className="flex flex-col gap-1.5 text-left">
      <label htmlFor={id} className="text-xs font-medium tracking-wide text-charcoal/60">
        Exakte Geburtszeit (HH:MM:SS){required ? " *" : ""}
      </label>
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="HH:MM:SS"
        maxLength={8}
        value={value}
        onChange={handleChange}
        className={`w-full rounded-xl border bg-white/90 px-3 py-2.5 font-mono text-charcoal outline-none transition-colors focus:bg-white placeholder:font-sans placeholder:text-slate-300 ${
          error
            ? "border-red-400 focus:border-red-400"
            : "border-slate-200 focus:border-gold"
        }`}
      />
      <p className={`text-xs ${error ? "text-red-500" : "text-charcoal/40"}`}>
        {error ?? (hint ?? "Für ein präzises Lagna zählt jede Sekunde.")}
      </p>
    </div>
  );
}
